import React from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Form, Button, Icon, TextArea, Message } from "semantic-ui-react";

const showListingInformationUsedAsFlyerDescriptionTooLongWarningMessage = (
  listingInformation,
  description,
  characterLimit,
) => {
  return (!description || description === "") && listingInformation.length > characterLimit;
};

const getDescriptionCharacterLimitExceededError = (listingInformation, description, characterLimit, errorMessage) => {
  if (description && description !== "" && description.length > characterLimit) {
    return { content: errorMessage };
  }

  return undefined;
};

const FlyerForm = observer(
  ({
    description,
    isDirty,
    onDescriptionChange,
    onSave,
    t,
    listingInformation,
    characterLimit,
    useTranslation,
    showSaveButton,
  }) => (
    <Form onSubmit={onSave}>
      {showListingInformationUsedAsFlyerDescriptionTooLongWarningMessage(
        listingInformation,
        description,
        characterLimit,
      ) &&
        useTranslation && (
          <Message warning style={{ display: "block" }}>
            <Message.Header>
              {t("clientPanelMisc:listingInformationUsedAsFlyerDescriptionTooLongWarningMessageHeader")}
            </Message.Header>
            <p>
              {t("clientPanelMisc:listingInformationUsedAsFlyerDescriptionTooLongWarningMessage", { characterLimit })}
            </p>
          </Message>
        )}
      {showListingInformationUsedAsFlyerDescriptionTooLongWarningMessage(
        listingInformation,
        description,
        characterLimit,
      ) &&
        !useTranslation && (
          <Message warning style={{ display: "block" }}>
            <Message.Header>Text exceeded allowed character limit for the selected flyer template</Message.Header>
            <p>
              Since there is no description provided below, the listing information will be used at the flyer. The text
              has exceeded the limit of allowed characters ({characterLimit}). The text will be shortened to fit the
              available space. If you want to have more control on the text of the flyer please provide a description
              below.
            </p>
          </Message>
        )}
      <Form.Field
        control={TextArea}
        label={useTranslation ? t("description") : "Description"}
        value={description || ""}
        onChange={event => onDescriptionChange(event.target.value)}
        error={getDescriptionCharacterLimitExceededError(
          listingInformation,
          description,
          characterLimit,
          useTranslation
            ? t("clientPanelMisc:flyerDescriptionLengthErrorMessage", { characterLimit })
            : `The description must be ${characterLimit} characters or less.`,
        )}
      />
      {showSaveButton && (
        <Button disabled={!isDirty} type="submit" primary>
          <Icon name="save" /> {useTranslation ? t("clientPanelMisc:saveFlyer") : "Save Flyer"}
        </Button>
      )}
    </Form>
  ),
);

FlyerForm.propTypes = {
  description: PropTypes.string,
  isDirty: PropTypes.bool,
  onDescriptionChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  listingInformation: PropTypes.string.isRequired,
  characterLimit: PropTypes.number.isRequired,
  // @todo Remove when we will find another solution for the translation at the provider panel
  useTranslation: PropTypes.bool,
  showSaveButton: PropTypes.bool,
};

FlyerForm.defaultProps = {
  useTranslation: false,
  showSaveButton: true,
};

export default withTranslation()(FlyerForm);
